import React, { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { API, formatApiErrorDetail } from "@/lib/api";
import FirstAccess from "@/pages/FirstAccess";
import AccessRequest from "@/pages/AccessRequest";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertCircle,
  BarChart3,
  Building2,
  Bug,
  CheckCircle2,
  Clock3,
  KeyRound,
  Loader2,
  MapPin,
  Search,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Map,
  Zap,
  Wheat,
  Activity,
  LayoutDashboard,
  MapPinned,
} from "lucide-react";

/*
 * ================================================================
 * ASTRAL CONTROL INTELLIGENCE
 * LOGIN + SOLICITAÇÃO DE ACESSO + PRIMEIRO ACESSO
 * ================================================================
 *
 * Esta versão mantém:
 * - login existente;
 * - recuperação de senha;
 * - branding existente;
 * - contas de demonstração;
 * - solicitação de acesso;
 * - pesquisa de empresa;
 * - seleção de unidade;
 * - perfil solicitado;
 * - informação de prazo configurável;
 * - confirmação visual da solicitação.
 *
 * Acrescenta:
 * - apresentação institucional dinâmica;
 * - áreas de atuação da ASTRAL;
 * - indicadores institucionais;
 * - transição visual após autenticação;
 * - sequência de carregamento do ambiente;
 * - animação de entrada;
 * - apresentação mais robusta da empresa.
 *
 * IMPORTANTE:
 *
 * Os endpoints públicos utilizados pela solicitação de acesso:
 *
 * GET  /auth/access-request/companies
 * POST /auth/access-request
 *
 * permanecem os mesmos.
 *
 * O login continua usando AuthContext.login().
 *
 * FLUXO DE ACESSO:
 *
 * 1. Login.jsx apresenta apenas o login normal e o link
 *    “Ainda não possui acesso? Solicitar acesso”.
 * 2. O link abre a tela oficial AccessRequest.jsx, onde ocorre
 *    somente o PRÉ-CADASTRO.
 * 3. O pré-cadastro é enviado ao administrador para análise.
 * 4. Após aprovação, o solicitante recebe o link/token do
 *    FirstAccess.jsx.
 * 5. O FirstAccess.jsx finaliza o cadastro, cria a senha e então
 *    o usuário retorna ao login para autenticar normalmente.
 *
 * A Login Page NÃO cria senha, NÃO cria usuário e NÃO duplica
 * o formulário de solicitação.
 *
 * ================================================================
 */

/*
 * ================================================================
 * APRESENTAÇÃO INSTITUCIONAL
 * ================================================================
 */

const INSTITUTIONAL_SLIDES = [
  {
    eyebrow: "EXPERIÊNCIA",
    value: "40+",
    title: "anos no controle de pragas",
    text:
      "A ASTRAL atua há mais de quatro décadas no mercado de controle de pragas e vetores urbanos.",
    icon: ShieldCheck,
  },
  {
    eyebrow: "ATUAÇÃO",
    value: "01",
    title: "gestão integrada",
    text:
      "Controle de insetos, roedores, cupins, mosquitos, sanitização, fumigação e outras soluções de saúde ambiental.",
    icon: Bug,
  },
  {
    eyebrow: "TECNOLOGIA",
    value: "360°",
    title: "operação orientada por dados",
    text:
      "O Control Intelligence transforma ocorrências, inspeções, SLA e ações em informação operacional.",
    icon: BarChart3,
  },
  {
    eyebrow: "EXCELÊNCIA",
    value: "ISO",
    title: "qualidade, meio ambiente e segurança",
    text:
      "A rede ASTRAL possui histórico de certificações e sistemas de gestão voltados à qualidade, meio ambiente e segurança.",
    icon: Sparkles,
  },
];


/*
 * ================================================================
 * ETAPAS DE AUTENTICAÇÃO
 * ================================================================
 */


const LOGIN_TRANSITION_STEPS = [
  {
    label: "Validando credenciais",
    sublabel:
      "Verificando seu acesso corporativo",
  },
  {
    label: "Acesso autorizado",
    sublabel:
      "Identificando seu ambiente operacional",
  },
  {
    label: "Carregando ambiente",
    sublabel:
      "Preparando seus módulos e permissões",
  },
  {
    label: "Tudo pronto",
    sublabel:
      "Abrindo o Control Intelligence",
  },
];


/*
 * ================================================================
 * UTILITÁRIOS
 * ================================================================
 */

function normalizeRole(role) {
  if (!role) {
    return "";
  }

  return String(role)
    .trim()
    .toLowerCase();
}

function roleLabel(role) {
  const normalized = normalizeRole(role);

  const labels = {
    admin: "Administrador",
    supervisor: "Supervisor",
    tecnico: "Técnico",
    gestor: "Gestor",
    cliente: "Cliente",
    responsavel: "Responsável",
  };

  return (
    labels[normalized] ||
    role ||
    "Usuário"
  );
}

function formatCompanyName(company) {
  if (!company) {
    return "";
  }

  return (
    company.name ||
    company.razao_social ||
    company.nome ||
    company.company_name ||
    company.trade_name ||
    company.fantasy_name ||
    ""
  );
}

function formatUnitName(unit) {
  if (!unit) {
    return "";
  }

  return (
    unit.name ||
    unit.nome ||
    unit.unit_name ||
    unit.unidade ||
    unit.location_name ||
    ""
  );
}

function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}

/*
 * ================================================================
 * NORMALIZAÇÃO DE EMPRESAS / UNIDADES
 * ================================================================
 *
 * A API pode retornar IDs e listas com nomes diferentes dependendo
 * da versão do backend. A Login Page normaliza esses formatos sem
 * alterar o contrato do endpoint.
 */

function getEntityId(entity) {
  if (!entity) return "";

  return (
    entity.id ||
    entity._id ||
    entity.company_id ||
    entity.unit_id ||
    entity.value ||
    ""
  );
}

function getCompanyUnits(company) {
  if (!company) return [];

  const candidates = [
    company.units,
    company.unidades,
    company.children,
    company.branches,
    company.locations,
    company.unit_list,
    company.unitList,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
}

function normalizeCompanyList(data) {
  const candidates = [
    data,
    data?.companies,
    data?.items,
    data?.results,
    data?.data,
    data?.data?.companies,
    data?.data?.items,
    data?.data?.results,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
}


/*
 * ================================================================
 * ILUSTRAÇÕES DO CONTROL INTELLIGENCE
 * ================================================================
 */

function ShowcaseIllustration({ variant }) {
  if (variant === "geospatial" || variant === "map") {
    const isMap = variant === "map";

    return (
      <div className="absolute inset-0 overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(16,185,129,0.18),transparent_35%),linear-gradient(135deg,#061018,#0b1b22)]" />

        <div className="absolute inset-[10%] rounded-[28px] border border-emerald-300/15 bg-white/[0.03] p-5 shadow-2xl backdrop-blur-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-emerald-300/70">
                CONTROL INTELLIGENCE
              </div>
              <div className="mt-1 text-lg font-black text-white">
                {isMap ? "Mapa operacional" : "Visão geoespacial"}
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[9px] text-white/55">
              LIVE
            </div>
          </div>

          <div className="relative mt-5 h-[68%] overflow-hidden rounded-2xl border border-white/10 bg-[#07161b]">
            <div className="absolute inset-0 opacity-40 [background-image:linear-gradient(rgba(255,255,255,.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.06)_1px,transparent_1px)] [background-size:28px_28px]" />
            <div className="absolute left-[12%] top-[18%] h-28 w-40 rotate-6 rounded-[38%_62%_52%_48%] border border-emerald-300/20 bg-emerald-300/5" />
            <div className="absolute right-[12%] top-[25%] h-32 w-44 -rotate-12 rounded-[62%_38%_48%_52%] border border-cyan-300/20 bg-cyan-300/5" />
            <div className="absolute bottom-[15%] left-[28%] h-28 w-52 rotate-3 rounded-[48%_52%_36%_64%] border border-white/10 bg-white/[0.02]" />

            {[
              [18, 26, "bg-emerald-400"],
              [36, 62, "bg-amber-300"],
              [52, 34, "bg-emerald-400"],
              [68, 70, "bg-red-400"],
              [82, 42, "bg-cyan-300"],
              [58, 82, "bg-emerald-400"],
            ].map(([left, top, color], index) => (
              <span
                key={index}
                className={`absolute h-3 w-3 rounded-full ${color} shadow-[0_0_18px_currentColor]`}
                style={{ left: `${left}%`, top: `${top}%` }}
              />
            ))}

            <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2">
              {["Monitorado", "Atenção", "Crítico"].map((label, index) => (
                <span
                  key={label}
                  className="rounded-full border border-white/10 bg-black/35 px-2.5 py-1 text-[8px] font-semibold text-white/65"
                >
                  <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${index === 1 ? "bg-amber-300" : index === 2 ? "bg-red-400" : "bg-emerald-400"}`} />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "species") {
    return (
      <div className="absolute inset-0 overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_25%,rgba(16,185,129,0.18),transparent_34%),linear-gradient(135deg,#061018,#0b1b22)]" />
        <div className="absolute inset-[10%] rounded-[28px] border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-emerald-300/70">ANÁLISE</div>
              <div className="mt-1 text-lg font-black text-white">Ocorrências por espécie</div>
            </div>
            <Bug className="text-emerald-300/70" size={22} />
          </div>

          <div className="mt-5 grid grid-cols-[1.05fr_.95fr] gap-4">
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <div className="flex h-40 items-end gap-3">
                {[58, 82, 44, 70, 36, 91, 62].map((height, index) => (
                  <div key={index} className="flex flex-1 flex-col justify-end gap-2">
                    <div className="rounded-t-md bg-emerald-400/65" style={{ height: `${height}%` }} />
                    <span className="text-center text-[7px] text-white/35">S{index + 1}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {[
                ["Moscas", "82", "bg-emerald-400"],
                ["Roedores", "64", "bg-cyan-300"],
                ["Baratas", "47", "bg-amber-300"],
                ["Outros", "28", "bg-violet-300"],
              ].map(([label, value, color]) => (
                <div key={label} className="rounded-xl border border-white/10 bg-black/20 p-3">
                  <div className="flex items-center justify-between text-[9px]">
                    <span className="text-white/55">{label}</span>
                    <strong className="text-white">{value}</strong>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/5">
                    <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(Number(value), 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "trend") {
    return (
      <div className="absolute inset-0 overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_75%,rgba(16,185,129,0.18),transparent_35%),linear-gradient(135deg,#061018,#0b1b22)]" />
        <div className="absolute inset-[10%] rounded-[28px] border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-emerald-300/70">HISTÓRICO</div>
              <div className="mt-1 text-lg font-black text-white">Evolução das ocorrências</div>
            </div>
            <Activity className="text-emerald-300/70" size={22} />
          </div>

          <div className="relative mt-5 h-48 overflow-hidden rounded-2xl border border-white/10 bg-black/20 p-4">
            <div className="absolute inset-x-4 top-1/4 border-t border-white/5" />
            <div className="absolute inset-x-4 top-1/2 border-t border-white/5" />
            <div className="absolute inset-x-4 top-3/4 border-t border-white/5" />
            <svg viewBox="0 0 600 180" className="absolute inset-4 h-[calc(100%-2rem)] w-[calc(100%-2rem)] overflow-visible">
              <defs>
                <linearGradient id="ciTrend" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0%" stopColor="#34d399" />
                  <stop offset="100%" stopColor="#22d3ee" />
                </linearGradient>
              </defs>
              <path
                d="M10 142 C70 128 88 105 135 112 S205 132 245 92 S315 54 350 72 S410 96 450 54 S515 28 590 42"
                fill="none"
                stroke="url(#ciTrend)"
                strokeWidth="5"
                strokeLinecap="round"
              />
              <path
                d="M10 142 C70 128 88 105 135 112 S205 132 245 92 S315 54 350 72 S410 96 450 54 S515 28 590 42 L590 180 L10 180 Z"
                fill="url(#ciTrend)"
                opacity="0.08"
              />
            </svg>
            <div className="absolute bottom-3 left-4 right-4 flex justify-between text-[8px] text-white/30">
              <span>JAN</span><span>MAR</span><span>MAI</span><span>JUL</span><span>SET</span><span>NOV</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-hidden bg-slate-950">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.20),transparent_35%),linear-gradient(135deg,#061018,#0b1b22)]" />
      <div className="absolute inset-[10%] rounded-[28px] border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-emerald-300/70">CONTROL INTELLIGENCE</div>
            <div className="mt-1 text-lg font-black text-white">Dashboard executivo</div>
          </div>
          <LayoutDashboard className="text-emerald-300/70" size={22} />
        </div>

        <div className="mt-5 grid grid-cols-4 gap-2">
          {[
            ["Capturas", "1.248", "+12%"],
            ["Armadilhas", "405", "+3%"],
            ["OS", "186", "+8%"],
            ["Setores", "42", "Ativo"],
          ].map(([label, value, delta]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="text-[7px] uppercase tracking-wider text-white/35">{label}</div>
              <div className="mt-2 text-lg font-black text-white">{value}</div>
              <div className="mt-1 text-[8px] text-emerald-300/75">{delta}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-[1.25fr_.75fr] gap-3">
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-semibold text-white/45">Atividade mensal</span>
              <span className="text-[8px] text-emerald-300/70">+18,4%</span>
            </div>
            <div className="mt-4 flex h-24 items-end gap-2">
              {[35, 52, 43, 68, 57, 78, 64, 88, 72, 92].map((height, index) => (
                <div key={index} className="flex-1 rounded-t-md bg-emerald-400/60" style={{ height: `${height}%` }} />
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <div className="text-[9px] font-semibold text-white/45">Status</div>
            <div className="mt-4 flex items-center justify-center">
              <div className="flex h-24 w-24 items-center justify-center rounded-full border-[10px] border-emerald-400/55 border-r-amber-300/50 border-b-white/10">
                <div className="text-center"><div className="text-xl font-black text-white">92%</div><div className="text-[7px] text-white/35">controle</div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/*
 * ================================================================
 * LOGIN
 * ================================================================
 */

function getSlideIcon(iconName) {
  const icons = {
    shield: ShieldCheck,
    shieldcheck: ShieldCheck,
    dashboard: LayoutDashboard,
    monitoramento: Activity,
    activity: Activity,
    pga: Wheat,
    wheat: Wheat,
    salas: Zap,
    salas_eletricas: Zap,
    geoespacial: MapPinned,
    map: Map,
    inteligencia: Sparkles,
    sparkles: Sparkles,
    bug: Bug,
  };

  return icons[
    String(iconName || "")
      .trim()
      .toLowerCase()
  ] || ShieldCheck;
}



const AREA_DETAILS = {
  "Roedores": {
    eyebrow: "Controle de roedores",
    title: "Prevenção, monitoramento e resposta rápida.",
    description:
      "Atuamos no controle integrado de roedores com inspeção técnica, identificação de pontos críticos, acompanhamento de evidências e histórico operacional.",
    highlights: ["Inspeção técnica", "Pontos críticos", "Histórico e evidências"],
    icon: Bug,
  },
  "Baratas": {
    eyebrow: "Controle de baratas",
    title: "Controle contínuo onde a operação mais precisa.",
    description:
      "Estruturamos ações de prevenção e controle para reduzir focos, acompanhar ocorrências e manter ambientes sob acompanhamento técnico.",
    highlights: ["Mapeamento de focos", "Tratamento", "Acompanhamento"],
    icon: Bug,
  },
  "Moscas": {
    eyebrow: "Controle de moscas",
    title: "Redução de focos e proteção da operação.",
    description:
      "Monitoramos condições que favorecem a presença de moscas e organizamos as ações de controle para que cada ocorrência tenha contexto e histórico.",
    highlights: ["Identificação de focos", "Prevenção", "Registro operacional"],
    icon: Activity,
  },
  "Mosquitos": {
    eyebrow: "Controle de mosquitos",
    title: "Prevenção orientada por risco e ambiente.",
    description:
      "Acompanhamos áreas suscetíveis, evidências e ocorrências para apoiar uma estratégia preventiva e contínua de controle.",
    highlights: ["Áreas críticas", "Evidências", "Acompanhamento"],
    icon: MapPinned,
  },
  "Cupins": {
    eyebrow: "Controle de cupins",
    title: "Proteção estrutural com acompanhamento técnico.",
    description:
      "Organizamos inspeções, registros e acompanhamento das áreas afetadas para dar visibilidade à evolução do controle.",
    highlights: ["Inspeção", "Registro", "Evolução do controle"],
    icon: ShieldCheck,
  },
  "Pragas de grãos": {
    eyebrow: "Pragas de grãos",
    title: "Monitoramento para operações sensíveis.",
    description:
      "Acompanhamos ocorrências relacionadas a pragas de grãos com foco em prevenção, evidências e controle operacional.",
    highlights: ["Monitoramento", "Prevenção", "Indicadores"],
    icon: Wheat,
  },
  "Sanitização": {
    eyebrow: "Sanitização",
    title: "Processos organizados para ambientes mais seguros.",
    description:
      "Integramos registros de execução e acompanhamento para transformar sanitização em um processo rastreável dentro da operação.",
    highlights: ["Execução", "Rastreabilidade", "Acompanhamento"],
    icon: ShieldCheck,
  },
  "Monitoramento": {
    eyebrow: "Monitoramento",
    title: "Visibilidade contínua da operação.",
    description:
      "Centralizamos inspeções, evidências, ocorrências e histórico para que equipes acompanhem o que está acontecendo por área e setor.",
    highlights: ["Inspeções", "Evidências", "Histórico"],
    icon: Activity,
  },
  "Salas elétricas": {
    eyebrow: "Salas elétricas",
    title: "Inspeções técnicas com histórico operacional.",
    description:
      "Organizamos inspeções de salas elétricas, evidências e indicadores em um fluxo único, com histórico para apoiar a tomada de decisão.",
    highlights: ["Inspeções técnicas", "Risco", "Relatórios"],
    icon: Zap,
  },
  "Geoespacial": {
    eyebrow: "Geoespacial",
    title: "A operação também pode ser vista no mapa.",
    description:
      "Conectamos informações operacionais ao contexto geográfico para facilitar a leitura de áreas, unidades e pontos de atenção.",
    highlights: ["Mapa operacional", "Unidades", "Pontos de atenção"],
    icon: Map,
  },
  "Inteligência ambiental": {
    eyebrow: "Inteligência ambiental",
    title: "Dados transformados em contexto para decisão.",
    description:
      "Reunimos informações operacionais e indicadores para ajudar equipes a entender tendências, priorizar ações e acompanhar resultados.",
    highlights: ["Indicadores", "Tendências", "Decisão"],
    icon: Sparkles,
  },
};

function LoginPage() {
  const navigate = useNavigate();

  const {
    user,
    loading: authLoading,
    login,
  } = useAuth();

  const {
    branding,
  } = useBranding();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");

  // Controla somente a preferência de persistência da sessão.
  // Não altera permissões, escopo, features ou configurações do usuário.
  const [rememberMe, setRememberMe] = React.useState(false);

  const [selectedArea, setSelectedArea] = React.useState("Roedores");

  const [error, setError] = React.useState("");

  const [loginLoading, setLoginLoading] =
    React.useState(false);

  const [showPassword, setShowPassword] =
    React.useState(false);

  const [
    transitionVisible,
    setTransitionVisible,
  ] = React.useState(false);

  const [
    transitionStep,
    setTransitionStep,
  ] = React.useState(0);

  const [
    showcaseSlide,
    setShowcaseSlide,
  ] = React.useState(0);

  const [
    loginPageConfig,
    setLoginPageConfig,
  ] = React.useState(null);

  const [
    showcasePaused,
    setShowcasePaused,
  ] = React.useState(false);

  const showcaseTimerRef =
    useRef(null);


  const [
    forgotOpen,
    setForgotOpen,
  ] = React.useState(false);

  const [
    forgotEmail,
    setForgotEmail,
  ] = React.useState("");

  const [
    forgotLoading,
    setForgotLoading,
  ] = React.useState(false);

  const [
    forgotSuccess,
    setForgotSuccess,
  ] = React.useState(false);

  const [
    forgotError,
    setForgotError,
  ] = React.useState("");


  const transitionTimerRef =
    useRef(null);

  /*
   * ================================================================
   * REDIRECIONAMENTO DO USUÁRIO JÁ AUTENTICADO
   * ================================================================
   */

  useEffect(() => {
    if (!authLoading && user) {
      navigate("/", {
        replace: true,
      });
    }
  }, [
    authLoading,
    user,
    navigate,
  ]);


  /*
   * ================================================================
   * REVELAÇÃO DURANTE O SCROLL
   * ---------------------------------------------------------------
   * O conteúdo entra e sai suavemente conforme o usuário percorre
   * a página. Como o estado "is-visible" é removido quando o bloco
   * sai da viewport, a animação também funciona ao voltar para cima.
   * ================================================================
   */

  useEffect(() => {
    const elements = Array.from(
      document.querySelectorAll("[data-scroll-reveal]")
    );

    if (!elements.length) return undefined;

    const reduceMotion =
      window.matchMedia?.(
        "(prefers-reduced-motion: reduce)"
      )?.matches;

    if (reduceMotion) {
      elements.forEach((element) => {
        element.classList.add("is-visible");
      });

      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = entry.target;

          if (entry.isIntersecting) {
            const enteringFromAbove =
              entry.boundingClientRect.top <
              window.innerHeight * 0.5;

            element.style.setProperty(
              "--astral-reveal-y",
              enteringFromAbove
                ? "-26px"
                : "26px"
            );

            element.style.setProperty(
              "--astral-reveal-scale",
              "0.985"
            );

            element.classList.add("is-visible");
          } else {
            element.classList.remove("is-visible");
          }
        });
      },
      {
        threshold: 0.08,
        rootMargin: "-8% 0px -8% 0px",
      }
    );

    elements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, []);


  /*
   * ================================================================
   * IDENTIDADE DA LOGIN PAGE
   * ================================================================
   *
   * A Login Page NÃO possui fotos pré-carregadas.
   * O carrossel existe, mas seu conteúdo é administrado
   * exclusivamente pelo Plataforma Admin.
   *
   * Toda a identidade visual vem exclusivamente do BrandingContext:
   * logo_main / logo_small, nomes institucionais, system_name,
   * login_message e login_bg.
   *
   * O módulo Marca é o responsável pela configuração visual.
   * ================================================================
   */

  /*
   * ================================================================
   * ENVIO DO LOGIN
   * ================================================================
   */

  async function handleLogin(
    event
  ) {
    event.preventDefault();

    if (loginLoading) {
      return;
    }

    setError("");

    const normalizedEmail =
      email.trim();

    if (!normalizedEmail) {
      setError(
        "Informe seu e-mail."
      );
      return;
    }

    if (!password) {
      setError(
        "Informe sua senha."
      );
      return;
    }

    setLoginLoading(true);

    try {
      const result =
        await login(
          normalizedEmail,
          password,
          rememberMe
        );

      if (
        result &&
        result.success === false
      ) {
        throw new Error(
          result.message ||
            "Não foi possível autenticar."
        );
      }

      setTransitionVisible(true);
      setTransitionStep(0);

      let currentStep = 0;

      transitionTimerRef.current =
        window.setInterval(
          () => {
            currentStep += 1;

            if (
              currentStep <
              LOGIN_TRANSITION_STEPS.length
            ) {
              setTransitionStep(
                currentStep
              );
            } else {
              if (
                transitionTimerRef.current
              ) {
                window.clearInterval(
                  transitionTimerRef.current
                );
              }

              navigate("/", {
                replace: true,
              });
            }
          },
          700
        );
    } catch (err) {
      console.error(
        "Erro no login:",
        err
      );

      setError(
        formatApiErrorDetail(
          err
        ) ||
          err?.message ||
          "E-mail ou senha inválidos."
      );
    } finally {
      setLoginLoading(false);
    }
  }


  /*
   * ================================================================
   * RECUPERAÇÃO DE SENHA
   * ================================================================
   */

  async function handleForgotPassword(
    event
  ) {
    event.preventDefault();

    if (forgotLoading) {
      return;
    }

    setForgotError("");
    setForgotSuccess(false);

    const normalizedEmail =
      forgotEmail.trim();

    if (!normalizedEmail) {
      setForgotError(
        "Informe seu e-mail."
      );
      return;
    }

    setForgotLoading(true);

    try {
      await axios.post(
        `${API}/auth/forgot-password`,
        {
          email:
            normalizedEmail,
        }
      );

      setForgotSuccess(true);
    } catch (err) {
      console.error(
        "Erro na recuperação de senha:",
        err
      );

      setForgotError(
        formatApiErrorDetail(
          err
        ) ||
          "Não foi possível solicitar a recuperação da senha."
      );
    } finally {
      setForgotLoading(false);
    }
  }


  /*
   * ================================================================
   * SLIDE ATUAL
   * ================================================================
   */


  /*
   * ================================================================
   * ESTILO DO BACKGROUND
   * ================================================================
   */

  const loginBackground =
    branding?.login_bg ||
    "";

  const brandLogo =
    branding?.logo_main ||
    branding?.logo_small ||
    "";

  const brandCompanyName =
    branding?.company_name ||
    branding?.header_name ||
    branding?.login_name ||
    "ASTRAL Saúde Ambiental";

  const brandLoginName =
    branding?.login_name ||
    branding?.company_name ||
    "ASTRAL";

  const brandSystemName =
    branding?.system_name ||
    "Control Intelligence";

  const brandLoginMessage =
    branding?.login_message ||
    "Gestão inteligente de saúde ambiental e controle de pragas.";

  const commercialEmail =
    branding?.commercial_email ||
    branding?.commercial_contact_email ||
    branding?.contact_email ||
    "";

  const commercialPhone =
    branding?.commercial_phone ||
    branding?.contact_phone ||
    "";

  /*
   * ================================================================
   * LOGIN PAGE / SLIDES ADMINISTRÁVEIS
   * ================================================================
   *
   * Não há fotos pré-carregadas ou hardcoded neste arquivo.
   * As imagens dos slides vêm exclusivamente da configuração
   * publicada pelo Plataforma Admin.
   */

  const activeSlides = useMemo(() => {
    const raw =
      Array.isArray(
        loginPageConfig?.branding_slides
      )
        ? loginPageConfig.branding_slides
        : Array.isArray(
            loginPageConfig?.slides
          )
          ? loginPageConfig.slides
          : [];

    return raw
      .filter(
        (slide) =>
          slide &&
          slide.enabled !== false
      )
      .map((slide, index) => ({
        ...slide,
        type: "branding",
        image:
          slide.image_url ||
          slide.image ||
          "",
        order: Number(
          slide.order ??
          index + 1
        ),
        duration: 4500,
      }))
      .sort(
        (a, b) =>
          a.order - b.order
      )
      .slice(0, 8);
  }, [loginPageConfig]);

  useEffect(() => {
    let cancelled = false;

    async function loadLoginPageConfig() {
      try {
        const { data } =
          await axios.get(
            `${API}/auth/login-page-config`,
            {
              timeout: 8000,
            }
          );

        if (
          !cancelled &&
          data
        ) {
          setLoginPageConfig(data);
        }
      } catch (err) {
        console.warn(
          "Não foi possível carregar a configuração da Login Page:",
          err
        );
      }
    }

    loadLoginPageConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeSlides.length) {
      setShowcaseSlide(0);
      return undefined;
    }

    if (
      showcaseSlide >=
      activeSlides.length
    ) {
      setShowcaseSlide(0);
    }

    return undefined;
  }, [
    activeSlides.length,
    showcaseSlide,
  ]);

  useEffect(() => {
    if (
      showcasePaused ||
      loginPageConfig?.autoplay === false ||
      !activeSlides.length
    ) {
      return undefined;
    }

    showcaseTimerRef.current =
      window.setTimeout(
        () => {
          setShowcaseSlide(
            (current) =>
              (
                current + 1
              ) %
              activeSlides.length
          );
        },
        4500
      );

    return () => {
      if (
        showcaseTimerRef.current
      ) {
        window.clearTimeout(
          showcaseTimerRef.current
        );
      }
    };
  }, [
    showcasePaused,
    showcaseSlide,
    activeSlides.length,
    loginPageConfig?.autoplay,
  ]);

  /*
   * ================================================================
   * RENDER
   * ================================================================
   */

  return (
    <div
      className="
        relative
        min-h-screen
        w-full
        overflow-x-clip
        scroll-smooth
        bg-[#03171b]
        selection:bg-emerald-300/20
        selection:text-white
        text-foreground
      "
    >

      {/* ============================================================
          CANVAS VISUAL FIXO
          ------------------------------------------------------------
          O fundo fica preso à viewport.
          O conteúdo é que percorre a página.
          ============================================================ */}

      <div
        className="
          pointer-events-none
          fixed
          inset-0
          z-0
          overflow-hidden
          bg-[#03171b]
        "
        aria-hidden="true"
      >

        {loginBackground && (
          <div
            className="
              absolute
              inset-0
              bg-cover
              bg-center
              opacity-25
            "
            style={{
              backgroundImage: `url("${loginBackground}")`,
            }}
          />
        )}

        <div
          className="
            absolute
            inset-0
            bg-[radial-gradient(circle_at_8%_8%,rgba(16,185,129,0.22),transparent_28%),radial-gradient(circle_at_78%_18%,rgba(20,184,166,0.17),transparent_30%),radial-gradient(circle_at_48%_72%,rgba(15,118,110,0.16),transparent_36%),linear-gradient(135deg,#02161b_0%,#063333_43%,#031b22_70%,#020d15_100%)]
          "
        />

        <div
          className="
            absolute
            -left-48
            top-[8%]
            h-[34rem]
            w-[34rem]
            rounded-full
            bg-emerald-400/10
            blur-[100px]
            animate-pulse
            astral-float
          "
        />

        <div
          className="
            absolute
            -right-56
            top-[28%]
            h-[40rem]
            w-[40rem]
            rounded-full
            bg-teal-300/10
            blur-[120px]
            animate-pulse
            astral-float
          "
        />

        <div
          className="
            absolute
            left-[38%]
            top-[52%]
            h-72
            w-72
            rounded-full
            bg-emerald-300/[0.07]
            blur-[90px]
            animate-pulse
          "
        />

        <div
          className="
            absolute
            inset-0
            bg-[linear-gradient(180deg,rgba(0,8,14,0.05)_0%,rgba(0,8,14,0.12)_55%,rgba(0,8,14,0.32)_100%)]
          "
        />

      </div>


      {/* ============================================================
          CONTEÚDO ROLÁVEL
          ------------------------------------------------------------
          Não há scroll interno. A página inteira se comporta como
          um site normal.
          ============================================================ */}

      <style>{`
        @keyframes astralFloat {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(0, -14px, 0) scale(1.025); }
        }

        @keyframes astralGlow {
          0%, 100% { opacity: .42; }
          50% { opacity: .78; }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes astralReveal {
          from {
            opacity: 0;
            transform:
              translate3d(0, var(--astral-reveal-y, 26px), 0)
              scale(var(--astral-reveal-scale, 0.985));
            filter: blur(3px);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0) scale(1);
            filter: blur(0);
          }
        }

        .astral-scroll-reveal {
          opacity: 0;
          transform:
            translate3d(0, var(--astral-reveal-y, 26px), 0)
            scale(var(--astral-reveal-scale, 0.985));
          filter: blur(3px);
          transition:
            opacity 650ms cubic-bezier(.22,1,.36,1),
            transform 750ms cubic-bezier(.22,1,.36,1),
            filter 650ms ease;
          will-change: opacity, transform, filter;
        }

        .astral-scroll-reveal.is-visible {
          opacity: 1;
          transform: translate3d(0, 0, 0) scale(1);
          filter: blur(0);
        }

        .astral-scroll-reveal[data-reveal-delay="1"] {
          transition-delay: 80ms;
        }

        .astral-scroll-reveal[data-reveal-delay="2"] {
          transition-delay: 150ms;
        }

        .astral-scroll-reveal[data-reveal-delay="3"] {
          transition-delay: 220ms;
        }

        .astral-scroll-reveal[data-reveal-delay="4"] {
          transition-delay: 290ms;
        }

        .astral-scroll-reveal[data-reveal-delay="5"] {
          transition-delay: 360ms;
        }

        .astral-scroll-reveal[data-reveal-delay="6"] {
          transition-delay: 430ms;
        }

        .astral-context-switch {
          animation: astralContextSwitch 520ms cubic-bezier(.22,1,.36,1) both;
        }

        @keyframes astralContextSwitch {
          from {
            opacity: 0;
            transform: translate3d(0, 10px, 0) scale(.985);
            filter: blur(3px);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0) scale(1);
            filter: blur(0);
          }
        }

        .astral-float {
          animation: astralFloat 8s ease-in-out infinite;
        }

        .astral-glow {
          animation: astralGlow 5s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .astral-float,
          .astral-glow {
            animation: none !important;
          }

          .astral-scroll-reveal {
            opacity: 1 !important;
            transform: none !important;
            filter: none !important;
            transition: none !important;
          }

          html {
            scroll-behavior: auto !important;
          }
        }
      `}</style>

      <div className="relative z-10">

        {/* ============================================================
            LAYOUT PRINCIPAL
            ============================================================ */}

      <div
        className="
          relative
          z-10
          min-h-screen
          w-full
          grid
          items-start
          lg:grid-cols-[1.18fr_0.82fr]
        "
      >

        {/* ==========================================================
            PAINEL INSTITUCIONAL
            ========================================================== */}

        <section
          id="topo-login"
          className="
            relative
            hidden
            lg:flex
            min-h-full
            overflow-visible
            bg-transparent
          "
        >

          {/* Fundo visual vem do canvas fixo global. */}

          <div
            className="
              relative
              z-20
              flex
              min-h-full
              w-full
              flex-col
              px-8
              py-8
              xl:px-12
            "
          >

            {/* ======================================================
                LOGO
                ====================================================== */}

            <div
              className="
                flex
                items-center
                gap-4
              "
            >

              <div
                className="
                  flex
                  shrink-0
                  items-center
                  justify-center
                  overflow-visible
                "
              >
                {brandLogo ? (
                  <img
                    src={brandLogo}
                    alt={brandCompanyName}
                    className="
                      max-h-12
                      max-w-48
                      object-contain
                    "
                    loading="eager"
                    decoding="async"
                    draggable="false"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <ShieldCheck
                    size={27}
                    strokeWidth={2}
                    className="text-primary"
                  />
                )}
              </div>

              <div>

                <div
                  className="
                    text-2xl
                    font-black
                    tracking-tight
                    text-white
                  "
                >
                  {brandLoginName}
                </div>

                <div
                  className="
                    mt-0.5
                    text-[10px]
                    font-semibold
                    uppercase
                    tracking-[0.24em]
                    text-white/65
                  "
                >
                  {brandCompanyName}
                </div>

              </div>

            </div>


            {/* ======================================================
                CONTEÚDO INSTITUCIONAL — FLUXO NORMAL
                ------------------------------------------------------
                O conteúdo acompanha a rolagem natural da página.
                ====================================================== */}

            <div
              className="
                flex
                w-full
                flex-col
                pt-10
                xl:pt-12
              "
            >

              <div
                className="
                  w-full
                  max-w-none
                "
              >

                {/* ==================================================
                    CARROSSEL DA LOGIN PAGE
                    ==================================================
                    Conteúdo exclusivamente administrado no
                    Plataforma Admin. Nenhuma foto é embutida
                    neste arquivo.
                    ================================================== */}

                {activeSlides.length > 0 && (
                  <div
                    className="
                      mb-7
                      w-full
                      max-w-none
                    "
                    onMouseEnter={() =>
                      setShowcasePaused(true)
                    }
                    onMouseLeave={() =>
                      setShowcasePaused(false)
                    }
                  >
                    {(() => {
                      const slide =
                        activeSlides[
                          showcaseSlide
                        ];

                      const SlideIcon =
                        getSlideIcon(
                          slide?.icon ||
                          slide?.icon_name ||
                          slide?.iconName
                        );

                      const hasImage =
                        Boolean(
                          slide?.image
                        );

                      return (
                        <div
                          className="
                            relative
                            overflow-hidden
                            rounded-2xl
                            border
                            border-transparent
                            bg-transparent
                          "
                        >
                          <div
                            className="
                              absolute
                              inset-0
                              bg-[radial-gradient(circle_at_75%_25%,rgba(16,185,129,0.08),transparent_34%)]
                              pointer-events-none
                            "
                          />

                          <div
                            className="
                              relative
                              flex
                              min-h-[154px]
                              items-center
                              gap-5
                              p-5
                              sm:p-6
                            "
                          >
                            <div
                              className="
                                flex
                                h-11
                                w-11
                                shrink-0
                                items-center
                                justify-center
                                text-primary
                              "
                            >
                              <SlideIcon
                                size={22}
                                strokeWidth={2}
                              />
                            </div>

                            <div
                              className="
                                min-w-0
                                flex-1
                              "
                            >
                              <div
                                className="
                                  flex
                                  items-center
                                  gap-2
                                "
                              >
                                <span
                                  className="
                                    text-[9px]
                                    font-bold
                      text-white
                                    uppercase
                                    tracking-[0.22em]
                                    text-primary
                                  "
                                >
                                  {slide?.eyebrow ||
                                    brandCompanyName}
                                </span>

                              </div>

                              <h2
                                className="
                                  mt-2
                                  text-2xl
                                  font-black
                                  leading-tight
                                  tracking-tight
                                  text-white
                                  sm:text-3xl
                                "
                              >
                                {slide?.title ||
                                  brandSystemName}
                              </h2>

                              {slide?.text && (
                                <p
                                  className="
                                    mt-2
                                    max-w-2xl
                                    text-sm
                                    leading-relaxed
                                    text-white/60
                                  "
                                >
                                  {slide.text}
                                </p>
                              )}
                            </div>

                            {hasImage && (
                              <div
                                className="
                                  hidden
                                  h-24
                                  w-36
                                  shrink-0
                                  overflow-hidden
                                  rounded-xl
                                  md:block
                                "
                              >
                                <img
                                  src={slide.image}
                                  alt={
                                    slide?.title ||
                                    "Imagem institucional"
                                  }
                                  className="
                                    h-full
                                    w-full
                                    object-cover
                                  "
                                  style={{
                                    objectPosition:
                                      slide?.object_position ||
                                      "center",
                                  }}
                                  loading="eager"
                                  decoding="async"
                                  draggable="false"
                                />
                              </div>
                            )}

                            <div
                              className="
                                absolute
                                right-5
                                top-5
                                text-[9px]
                                font-semibold
                                tracking-[0.16em]
                                text-white/35
                              "
                            >
                              {String(
                                showcaseSlide + 1
                              ).padStart(2, "0")}
                              {" / "}
                              {String(
                                activeSlides.length
                              ).padStart(2, "0")}
                            </div>
                          </div>

                          {activeSlides.length > 1 && (
                            <div
                              className="
                                relative
                                flex
                                items-center
                                gap-1.5
                                px-5
                                py-3
                                sm:px-6
                              "
                            >
                              <div
                                className="
                                  flex
                                  flex-1
                                  items-center
                                  gap-1.5
                                "
                              >
                                {activeSlides.map(
                                  (
                                    item,
                                    index
                                  ) => (
                                    <button
                                      key={
                                        item.id ||
                                        `${item.title || "slide"}-${index}`
                                      }
                                      type="button"
                                      onClick={() =>
                                        setShowcaseSlide(
                                          index
                                        )
                                      }
                                      aria-label={
                                        `Mostrar ${item.title || `slide ${index + 1}`}`
                                      }
                                      className="
                                        group
                                        flex-1
                                        py-1
                                      "
                                    >
                                      <span
                                        className={`
                                          block
                                          h-1
                                          rounded-full
                                          transition-all
                                          ${
                                            index ===
                                            showcaseSlide
                                              ? "bg-primary"
                                              : "bg-white/10 group-hover:bg-white/25"
                                          }
                                        `}
                                      />
                                    </button>
                                  )
                                )}
                              </div>

                              <span
                                className="
                                  ml-3
                                  text-[8px]
                                  uppercase
                                  tracking-[0.18em]
                                  text-white/30
                                "
                              >
                                4,5s
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* ==================================================
                    CONTEXTO INSTITUCIONAL DO HERO
                    ================================================== */}

                <div
                  data-scroll-reveal
                  data-reveal-delay="1"
                  className="
                    mb-5
                    grid
                    max-w-4xl
                    gap-2
                    sm:grid-cols-3
                    astral-scroll-reveal
                  "
                >
                  {[
                    ["PREVENÇÃO", "Atuação técnica antes da infestação."],
                    ["MONITORAMENTO", "Dados para acompanhar criticidade e tendência."],
                    ["RESULTADOS", "Processos que transformam controle em gestão."],
                  ].map(([title, description]) => (
                    <div
                      key={title}
                      className="
                        rounded-xl
                        border
                        border-white/[0.07]
                        bg-black/20
                        px-4
                        py-3
                        backdrop-blur
                      "
                    >
                      <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-emerald-300/70">
                        {title}
                      </div>
                      <div className="mt-1 text-[11px] leading-5 text-white/50">
                        {description}
                      </div>
                    </div>
                  ))}
                </div>


                {/* ==================================================
                    TÍTULO
                    ================================================== */}

                <div
                  className="
                    max-w-3xl
                  "
                >

                  <div
                    className="
                      inline-flex
                      items-center
                      gap-2
                      rounded-full
                      border
                      border-white/10
                      bg-black/25
                      px-3
                      py-1.5
                      text-[10px]
                      font-semibold
                      uppercase
                      tracking-[0.18em]
                      text-white/70
                      backdrop-blur
                    "
                  >
                    <span
                      className="
                        h-1.5
                        w-1.5
                        rounded-full
                        bg-primary
                      "
                    />

                    SAÚDE AMBIENTAL
                    {" • "}
                    TECNOLOGIA
                    {" • "}
                    OPERAÇÃO
                  </div>

                  <h1
                    className="
                      mt-5
                      text-6xl
                      font-black
                      leading-[0.95]
                      tracking-[-0.045em]
                      text-white
                      xl:text-7xl
                    "
                  >
                    {brandSystemName}.
                  </h1>

                  <p
                    className="
                      mt-5
                      max-w-2xl
                      text-lg
                      leading-relaxed
                      text-white/75
                      xl:text-xl
                    "
                  >
                    {brandLoginMessage}
                  </p>

                </div>


                {/* ==================================================
                    MÓDULOS
                    ================================================== */}

                <div
                  className="
                    mt-8
                    max-w-3xl
                  "
                >

                  <div
                    className="
                      mb-3
                      text-[10px]
                      font-bold
                      uppercase
                      tracking-[0.22em]
                      text-white/45
                    "
                  >
                    INTELIGÊNCIA OPERACIONAL
                  </div>

                  <div
                    className="
                      grid
                      grid-cols-2
                      gap-2
                      md:grid-cols-3
                    "
                  >

                    {[
                      {
                        icon:
                          LayoutDashboard,
                        title:
                          "Dashboard",
                        text:
                          "Indicadores de gestão",
                      },
                      {
                        icon:
                          Activity,
                        title:
                          "Monitoramento",
                        text:
                          "Por área e setor",
                      },
                      {
                        icon:
                          Wheat,
                        title:
                          "PGA",
                        text:
                          "Pragas de grãos",
                      },
                      {
                        icon:
                          Zap,
                        title:
                          "Salas elétricas",
                        text:
                          "Inspeções técnicas",
                      },
                      {
                        icon:
                          MapPinned,
                        title:
                          "Geoespacial",
                        text:
                          "Mapa operacional",
                      },
                      {
                        icon:
                          Sparkles,
                        title:
                          "Inteligência",
                        text:
                          "Visão integrada",
                      },
                    ].map(
                      (
                        item
                      ) => {
                        const Icon =
                          item.icon;

                        return (
                          <div
                            key={
                              item.title
                            }
                            className="
                              flex
                              items-center
                              gap-3
                              rounded-xl
                              border
                              border-white/10
                              bg-black/25
                              px-3
                              py-2.5
                              backdrop-blur
                            "
                          >

                            <div
                              className="
                                flex
                                h-8
                                w-8
                                shrink-0
                                items-center
                                justify-center
                                rounded-lg
                                bg-primary/10
                                text-primary
                              "
                            >
                              <Icon
                                size={15}
                              />
                            </div>

                            <div
                              className="
                                min-w-0
                              "
                            >

                              <div
                                className="
                                  truncate
                                  text-xs
                                  font-bold
                                  text-white
                                "
                              >
                                {
                                  item.title
                                }
                              </div>

                              <div
                                className="
                                  truncate
                                  text-[10px]
                                  text-white/45
                                "
                              >
                                {
                                  item.text
                                }
                              </div>

                            </div>

                          </div>
                        );
                      }
                    )}

                  </div>

                </div>


                {/* ==================================================
                    TAGS
                    ================================================== */}

                <div
                  className="
                    mt-6
                    flex
                    max-w-4xl
                    flex-wrap
                    gap-2
                  "
                >

                  {[
                    "Roedores",
                    "Baratas",
                    "Moscas",
                    "Mosquitos",
                    "Cupins",
                    "Pragas de grãos",
                    "Sanitização",
                    "Monitoramento",
                  ].map(
                    (
                      tag
                    ) => (
                      <span
                        key={tag}
                        className="
                          rounded-full
                          border
                          border-white/10
                          bg-black/25
                          px-3
                          py-1.5
                          text-[10px]
                          font-medium
                          text-white/70
                          backdrop-blur
                        "
                      >
                        {tag}
                      </span>
                    )
                  )}

                </div>

              </div>

            </div>


            {/* ======================================================
                NAVEGAÇÃO INSTITUCIONAL
                ====================================================== */}

            <nav
              className="
                mt-12
                flex
                flex-wrap
                gap-2
                scroll-mt-8
              "
              aria-label="Navegação institucional"
            >
              {[
                ["#quem-somos", "Quem somos"],
                ["#o-que-fazemos", "O que fazemos"],
                ["#metodologia", "Metodologia"],
                ["#areas-de-atuacao", "Áreas de atuação"],
                ["#segmentos", "Segmentos"],
                ["#a-plataforma", "A plataforma"],
                ["#fale-conosco", "Fale conosco"],
              ].map(([href, label]) => (
                <a
                  key={href}
                  href={href}
                  className="
                    rounded-full
                    bg-white/[0.035]
                    px-4
                    py-2
                    text-[10px]
                    font-semibold
                    uppercase
                    tracking-[0.12em]
                    text-white/65
                    transition-all
                    duration-300
                    hover:-translate-y-0.5
                    hover:bg-emerald-400/10
                    hover:text-emerald-200
                  "
                >
                  {label}
                </a>
              ))}
            </nav>


            {/* ======================================================
                QUEM SOMOS
                ====================================================== */}

            <section
              id="quem-somos"
              data-scroll-reveal
              className="
                mt-16
                scroll-mt-8
                astral-scroll-reveal
              "
            >
              <div className="max-w-4xl">
                <div
                  className="
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.22em]
                    text-emerald-300/80
                  "
                >
                  ASTRAL SAÚDE AMBIENTAL
                </div>

                <h2
                  className="
                    mt-3
                    text-3xl
                    font-black
                    tracking-tight
                    text-white
                    sm:text-4xl
                  "
                >
                  Expertise, processos e resultados para ambientes mais seguros e produtivos.
                </h2>

                <p
                  className="
                    mt-5
                    max-w-3xl
                    text-sm
                    leading-7
                    text-white/65
                    sm:text-base
                  "
                >
                  No ambiente agroindustrial, controle de pragas não é apenas
                  uma questão de aplicação de produtos. É uma estratégia
                  essencial para proteger matérias-primas, produtos acabados,
                  instalações, processos, pessoas e, principalmente, a segurança
                  e a qualidade dos alimentos.
                </p>

                <p
                  className="
                    mt-4
                    max-w-3xl
                    text-sm
                    leading-7
                    text-white/55
                    sm:text-base
                  "
                >
                  A ASTRAL SAÚDE AMBIENTAL atua no controle integrado de pragas
                  com uma abordagem técnica, preventiva e orientada a resultados,
                  desenvolvendo soluções de acordo com as características, os
                  riscos e os desafios de cada operação.
                </p>
              </div>
              <div
                data-scroll-reveal
                data-reveal-delay="2"
                className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-4 astral-scroll-reveal"
              >
                {[
                  ["40+", "Experiência", "Conhecimento técnico aplicado à operação."],
                  ["MIP", "Prevenção", "Manejo integrado com monitoramento contínuo."],
                  ["360°", "Gestão", "Visão integrada de ocorrências e indicadores."],
                  ["ISO", "Qualidade", "Histórico de gestão, qualidade, meio ambiente e segurança."],
                ].map(([value, title, description]) => (
                  <div key={title} className="rounded-2xl border border-white/[0.06] bg-black/15 p-4">
                    <div className="text-2xl font-black text-white">{value}</div>
                    <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-300/75">{title}</div>
                    <p className="mt-2 text-[11px] leading-5 text-white/40">{description}</p>
                  </div>
                ))}
              </div>

              <div
                data-scroll-reveal
                data-reveal-delay="2"
                className="
                  mt-7
                  grid
                  gap-3
                  sm:grid-cols-3
                  astral-scroll-reveal
                "
              >
                {[
                  ["Experiência técnica", "Conhecimento aplicado à realidade de cada operação."],
                  ["Prevenção", "Identificação de causas, redução de riscos e barreiras permanentes."],
                  ["Resultados", "Indicadores para acompanhar tendências, ações e evolução."],
                ].map(([title, description]) => (
                  <article
                    key={title}
                    className="
                      rounded-2xl
                      bg-white/[0.025]
                      p-5
                      transition-all
                      duration-300
                      hover:-translate-y-1
                      hover:bg-white/[0.05]
                    "
                  >
                    <div className="h-1 w-8 rounded-full bg-emerald-400/70" />
                    <h3 className="mt-4 text-sm font-bold text-white">
                      {title}
                    </h3>
                    <p className="mt-2 text-xs leading-6 text-white/45">
                      {description}
                    </p>
                  </article>
                ))}
              </div>
            </section>


            {/* ======================================================
                ESPECIALIZAÇÃO AGROINDUSTRIAL
                ====================================================== */}

            <section
              id="o-que-fazemos"
              data-scroll-reveal
              className="
                mt-16
                scroll-mt-8
                astral-scroll-reveal
              "
            >
              <div className="max-w-4xl">
                <div
                  className="
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.22em]
                    text-emerald-300/80
                  "
                >
                  Especialização que entende a complexidade da agroindústria
                </div>

                <h2
                  className="
                    mt-3
                    text-3xl
                    font-black
                    tracking-tight
                    text-white
                    sm:text-4xl
                  "
                >
                  Mais do que responder à ocorrência: entender a causa e proteger a operação.
                </h2>

                <p className="mt-4 text-sm leading-7 text-white/60 sm:text-base">
                  Operações agroindustriais apresentam condições altamente
                  favoráveis à presença e proliferação de pragas: disponibilidade
                  de alimento, áreas de armazenamento, fluxo intenso de pessoas
                  e materiais, variações de temperatura e umidade, além de
                  estruturas com diferentes níveis de criticidade.
                </p>

                <p className="mt-4 text-sm leading-7 text-white/55 sm:text-base">
                  Por isso, a ASTRAL trabalha além da resposta imediata à
                  ocorrência. Nossa atuação busca identificar as causas, reduzir
                  fatores de risco e estabelecer barreiras permanentes para o
                  controle das infestações.
                </p>
              </div>

              <div
                data-scroll-reveal
                data-reveal-delay="2"
                className="
                  mt-7
                  grid
                  gap-3
                  sm:grid-cols-2
                  lg:grid-cols-4
                  astral-scroll-reveal
                "
              >
                {[
                  [Bug, "Diagnóstico", "Condições ambientais e estruturais."],
                  [MapPinned, "Pontos críticos", "Identificação e mapeamento das áreas de atenção."],
                  [Activity, "Monitoramento", "Acompanhamento sistemático das áreas críticas."],
                  [BarChart3, "Evolução", "Indicadores para medir ações e resultados."],
                ].map(([Icon, title, description]) => (
                  <article
                    key={title}
                    className="
                      rounded-2xl
                      border
                      border-white/[0.06]
                      bg-white/[0.025]
                      p-4
                    "
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-300">
                      <Icon size={17} />
                    </div>
                    <h3 className="mt-4 text-sm font-bold text-white">{title}</h3>
                    <p className="mt-2 text-xs leading-5 text-white/45">{description}</p>
                  </article>
                ))}
              </div>
            </section>


            {/* ======================================================
                METODOLOGIA
                ====================================================== */}

            <section
              id="metodologia"
              data-scroll-reveal
              className="
                mt-16
                scroll-mt-8
                astral-scroll-reveal
              "
            >
              <div
                className="
                  rounded-[28px]
                  bg-[linear-gradient(135deg,rgba(16,185,129,0.08),rgba(255,255,255,0.02))]
                  p-6
                  sm:p-8
                "
              >
                <div className="max-w-3xl">
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                    Processo técnico, controle inteligente
                  </div>

                  <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">
                    Controle de pragas como processo contínuo de gestão.
                  </h2>

                  <p className="mt-4 text-sm leading-7 text-white/60">
                    Nosso diferencial está na combinação entre expertise técnica,
                    metodologia, monitoramento e gestão de informações. Cada
                    operação é avaliada de forma individualizada.
                  </p>
                </div>

                <div
                  data-scroll-reveal
                  data-reveal-delay="2"
                  className="mt-7 grid gap-2 sm:grid-cols-2 xl:grid-cols-3 astral-scroll-reveal"
                >
                  {[
                    "Diagnóstico das condições ambientais e estruturais",
                    "Identificação e mapeamento dos pontos críticos",
                    "Análise dos níveis e padrões de infestação",
                    "Definição de estratégias de controle e prevenção",
                    "Utilização técnica e responsável de métodos e produtos adequados",
                    "Monitoramento sistemático das áreas críticas",
                    "Registro e rastreabilidade das intervenções",
                    "Análise dos indicadores e evolução dos resultados",
                    "Ações corretivas e preventivas orientadas por evidências",
                  ].map((item, index) => (
                    <div
                      key={item}
                      className="
                        flex
                        items-start
                        gap-3
                        rounded-xl
                        bg-black/15
                        px-4
                        py-3
                      "
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-400/10 text-[9px] font-bold text-emerald-300">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="text-xs leading-5 text-white/60">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-7 rounded-2xl bg-emerald-400/[0.06] p-5">
                  <div className="text-sm font-bold text-white">
                    O resultado é uma operação mais previsível.
                  </div>
                  <p className="mt-2 text-xs leading-6 text-white/50">
                    A metodologia transforma o controle de pragas em um
                    processo contínuo de gestão, e não em uma simples
                    prestação de serviço.
                  </p>
                </div>
              </div>
            </section>


            {/* ======================================================
                MIP
                ====================================================== */}

            <section
              data-scroll-reveal
              className="mt-16 astral-scroll-reveal"
            >
              <div className="grid gap-5 lg:grid-cols-[1.1fr_.9fr]">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                    Controle Integrado de Pragas
                  </div>

                  <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">
                    Prevenção antes da infestação.
                  </h2>

                  <p className="mt-4 text-sm leading-7 text-white/60">
                    A ASTRAL SAÚDE AMBIENTAL atua com os princípios do
                    Manejo Integrado de Pragas (MIP), priorizando a prevenção,
                    o monitoramento e a tomada de decisão baseada em dados.
                  </p>

                  <p className="mt-4 text-sm leading-7 text-white/55">
                    O objetivo é estabelecer um sistema de proteção que
                    dificulte a entrada, o abrigo, a alimentação e a reprodução
                    de pragas, reduzindo a dependência de intervenções
                    corretivas e aumentando a previsibilidade do controle.
                  </p>
                </div>

                <div
                  className="
                    rounded-[26px]
                    bg-black/20
                    p-6
                  "
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/35">
                    Proteção da cadeia operacional
                  </div>

                  <div className="mt-5 space-y-2">
                    {[
                      "Áreas externas",
                      "Recebimento de matérias-primas",
                      "Produção",
                      "Armazenagem",
                      "Expedição",
                      "Áreas de apoio",
                    ].map((item, index) => (
                      <div
                        key={item}
                        className="flex items-center gap-3 rounded-xl bg-white/[0.025] px-3 py-2.5"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                        <span className="text-xs text-white/60">{item}</span>
                        <span className="ml-auto text-[9px] text-white/25">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>


            {/* ======================================================
                RESULTADOS
                ====================================================== */}

            <section
              data-scroll-reveal
              className="mt-16 astral-scroll-reveal"
            >
              <div className="max-w-4xl">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                  Resultados que podem ser medidos
                </div>

                <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">
                  Eficiência é controlar tendências, prevenir reincidências e demonstrar evolução.
                </h2>

                <p className="mt-4 text-sm leading-7 text-white/60 sm:text-base">
                  Para a ASTRAL, eficiência não significa apenas eliminar uma
                  ocorrência. Resultado é reduzir riscos, controlar tendências,
                  prevenir reincidências e demonstrar a evolução do programa.
                </p>

                <p className="mt-4 text-sm leading-7 text-white/55 sm:text-base">
                  Por meio de monitoramento e indicadores, é possível acompanhar
                  o comportamento das ocorrências, identificar áreas de maior
                  criticidade, avaliar a efetividade das ações implementadas e
                  direcionar recursos para onde eles realmente são necessários.
                </p>
              </div>

              <div className="mt-7 grid gap-3 sm:grid-cols-3">
                {[
                  [BarChart3, "Indicadores", "Acompanhamento do comportamento das ocorrências."],
                  [MapPinned, "Criticidade", "Identificação das áreas que exigem maior atenção."],
                  [ShieldCheck, "Previsibilidade", "Mais controle operacional, rastreabilidade e segurança."],
                ].map(([Icon, title, description]) => (
                  <article key={title} className="rounded-2xl bg-white/[0.025] p-5">
                    <Icon size={19} className="text-emerald-300" />
                    <h3 className="mt-4 text-sm font-bold text-white">{title}</h3>
                    <p className="mt-2 text-xs leading-6 text-white/45">{description}</p>
                  </article>
                ))}
              </div>
            </section>


            {/* ======================================================
                ÁREAS DE ATUAÇÃO
                ====================================================== */}

            <section
              id="areas-de-atuacao"
              data-scroll-reveal
              className="
                mt-16
                scroll-mt-8
                astral-scroll-reveal
              "
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                Áreas de atuação
              </div>

              <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">
                Soluções para diferentes ambientes e níveis de complexidade.
              </h2>

              <p className="mt-4 max-w-3xl text-sm leading-7 text-white/55">
                Navegue pelos principais contextos de controle e monitoramento
                que podem ser acompanhados dentro da operação.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {Object.keys(AREA_DETAILS).map((tag) => {
                  const active = selectedArea === tag;

                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => {
                        setSelectedArea(tag);
                        window.requestAnimationFrame(() => {
                          document
                            .getElementById("especialidade-astral")
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                        });
                      }}
                      className={`
                        rounded-full
                        border
                        px-4
                        py-2
                        text-xs
                        transition-all
                        duration-300
                        hover:-translate-y-0.5
                        ${
                          active
                            ? "border-emerald-300/30 bg-emerald-400/15 text-emerald-100 shadow-[0_0_24px_rgba(16,185,129,0.10)]"
                            : "border-white/[0.06] bg-white/[0.035] text-white/65 hover:border-emerald-300/20 hover:bg-emerald-400/10 hover:text-emerald-200"
                        }
                      `}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </section>


            {/* ======================================================
                SEGMENTOS
                ====================================================== */}

            <section
              id="segmentos"
              data-scroll-reveal
              className="mt-16 scroll-mt-8 astral-scroll-reveal"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                Soluções para diferentes segmentos
              </div>

              <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">
                Programas de controle alinhados à realidade de cada ambiente.
              </h2>

              <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  {
                    icon: Wheat,
                    title: "Agroindústrias e indústrias de alimentos",
                    text: "Foco na proteção do processo produtivo e na segurança dos alimentos.",
                  },
                  {
                    icon: Building2,
                    title: "Armazéns, silos e unidades de armazenamento",
                    text: "Estratégias voltadas à prevenção de perdas e ao controle de pontos críticos.",
                  },
                  {
                    icon: MapPinned,
                    title: "Indústrias e centros de distribuição",
                    text: "Proteção de produtos, estruturas e fluxos logísticos.",
                  },
                  {
                    icon: ShieldCheck,
                    title: "Empresas, condomínios e instituições",
                    text: "Programas adequados às características específicas de cada ambiente.",
                  },
                ].map((item) => {
                  const Icon = item.icon;

                  return (
                    <article
                      key={item.title}
                      className="
                        group
                        rounded-2xl
                        bg-white/[0.025]
                        p-5
                        transition-all
                        duration-300
                        hover:-translate-y-1
                        hover:bg-white/[0.05]
                      "
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-300">
                        <Icon size={19} />
                      </div>
                      <h3 className="mt-4 text-sm font-bold leading-5 text-white">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-xs leading-6 text-white/45">
                        {item.text}
                      </p>
                    </article>
                  );
                })}
              </div>
            </section>


            {/* ======================================================
                A PLATAFORMA
                ====================================================== */}

            <section
              id="a-plataforma"
              data-scroll-reveal
              className="mt-16 scroll-mt-8 astral-scroll-reveal"
            >
              <div
                className="
                  rounded-[28px]
                  bg-[linear-gradient(135deg,rgba(16,185,129,0.09),rgba(255,255,255,0.025))]
                  p-6
                  sm:p-8
                "
              >
                <div className="max-w-3xl">
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                    Tecnologia aplicada à operação
                  </div>

                  <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">
                    Uma visão integrada para transformar informação em decisão.
                  </h2>

                  <p className="mt-4 text-sm leading-7 text-white/60">
                    O Control Intelligence reúne dados operacionais em um único
                    ambiente para que equipes acompanhem ocorrências, inspeções,
                    evidências, indicadores e histórico com mais contexto.
                  </p>
                </div>

                <div className="mt-7 grid gap-3 sm:grid-cols-3">
                  {[
                    ["01", "Centralizar", "Informações em um único ambiente."],
                    ["02", "Monitorar", "Acompanhar a operação e seus pontos críticos."],
                    ["03", "Decidir", "Transformar dados e evidências em ação."],
                  ].map(([number, title, description]) => (
                    <div key={number} className="rounded-2xl bg-black/15 p-4">
                      <div className="text-[10px] font-bold tracking-[0.18em] text-emerald-300/60">
                        {number}
                      </div>
                      <div className="mt-2 text-sm font-bold text-white">{title}</div>
                      <div className="mt-1 text-xs leading-5 text-white/45">{description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>


            {/* ======================================================
                FALE CONOSCO
                ====================================================== */}

            <section
              id="fale-conosco"
              data-scroll-reveal
              className="mt-16 scroll-mt-8 astral-scroll-reveal"
            >
              <div
                className="
                  relative
                  overflow-hidden
                  rounded-[30px]
                  border
                  border-emerald-300/15
                  bg-[linear-gradient(135deg,rgba(16,185,129,0.12),rgba(255,255,255,0.025))]
                  p-6
                  shadow-[0_25px_70px_rgba(0,0,0,0.18)]
                  sm:p-8
                "
              >
                <div className="absolute -right-16 -top-20 h-48 w-48 rounded-full bg-emerald-400/10 blur-3xl" />

                <div className="relative grid gap-7 lg:grid-cols-[1fr_auto] lg:items-center">
                  <div className="max-w-2xl">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                      Fale conosco
                    </div>

                    <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">
                      Mais do que controlar pragas. Proteger operações.
                    </h2>

                    <p className="mt-4 text-sm leading-7 text-white/60">
                      A ASTRAL SAÚDE AMBIENTAL combina conhecimento técnico,
                      processos estruturados, tecnologia de monitoramento e
                      acompanhamento de resultados para entregar soluções que
                      fazem sentido para cada operação.
                    </p>

                    <p className="mt-3 text-sm leading-7 text-white/50">
                      Fale com a ASTRAL para solicitar um orçamento, apresentar
                      sua operação, solicitar um serviço ou entender qual
                      programa de controle atende melhor aos seus riscos.
                    </p>

                    <div className="mt-6 flex flex-wrap gap-2">
                      {["Solicitar orçamento", "Solicitar serviço", "Conhecer soluções"].map((label) => (
                        <span
                          key={label}
                          className="rounded-full border border-white/[0.08] bg-black/15 px-3 py-1.5 text-[10px] font-semibold text-white/55"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="relative flex flex-col gap-3 sm:flex-row lg:flex-col">
                    {commercialEmail ? (
                      <a
                        href={`mailto:${commercialEmail}?subject=Contato comercial - ASTRAL Saúde Ambiental`}
                        className="
                          inline-flex
                          min-w-48
                          items-center
                          justify-center
                          gap-2
                          rounded-xl
                          border
                          border-emerald-300/25
                          bg-emerald-400/15
                          px-5
                          py-3
                          text-sm
                          font-bold
                          text-emerald-100
                          transition-all
                          duration-300
                          hover:-translate-y-0.5
                          hover:bg-emerald-400/20
                        "
                      >
                        <UserPlus size={17} />
                        Fale com a ASTRAL
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          document
                            .getElementById("login-email")
                            ?.focus();
                        }}
                        className="
                          inline-flex
                          min-w-48
                          items-center
                          justify-center
                          gap-2
                          rounded-xl
                          border
                          border-emerald-300/25
                          bg-emerald-400/15
                          px-5
                          py-3
                          text-sm
                          font-bold
                          text-emerald-100
                          transition-all
                          duration-300
                          hover:-translate-y-0.5
                          hover:bg-emerald-400/20
                        "
                      >
                        <UserPlus size={17} />
                        Fale com a ASTRAL
                      </button>
                    )}

                    {commercialPhone && (
                      <a
                        href={`tel:${commercialPhone.replace(/[^0-9+]/g, "")}`}
                        className="
                          inline-flex
                          min-w-48
                          items-center
                          justify-center
                          gap-2
                          rounded-xl
                          border
                          border-white/[0.08]
                          bg-black/15
                          px-5
                          py-3
                          text-sm
                          font-semibold
                          text-white/70
                          transition-all
                          duration-300
                          hover:bg-white/[0.06]
                          hover:text-white
                        "
                      >
                        <MapPinned size={17} />
                        Ligar para a ASTRAL
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </section>


            {/* ======================================================
                VOLTAR AO TOPO
                ====================================================== */}

            <div
              data-scroll-reveal
              data-reveal-delay="2"
              className="
                mt-12
                flex
                w-full
                items-center
                justify-between
                gap-4
                border-t
                border-white/[0.06]
                pt-5
                astral-scroll-reveal
              "
            >
              <span
                className="
                  text-[10px]
                  uppercase
                  tracking-[0.14em]
                  text-white/30
                "
              >
                Control Intelligence
              </span>

              <a
                href="#topo-login"
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-white/[0.07]
                  bg-white/[0.035]
                  px-4
                  py-2
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[0.14em]
                  text-white/50
                  backdrop-blur
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:border-emerald-300/15
                  hover:bg-emerald-400/[0.06]
                  hover:text-emerald-200
                "
              >
                Voltar ao início
                <span aria-hidden="true">↑</span>
              </a>
            </div>


            {/* ======================================================
                RODAPÉ
                ====================================================== */}

            <div
              className="
                flex
                items-center
                justify-between
                gap-5
                text-[10px]
                text-white/45
              "
            >

              <span>
                © 2026 Astral Saúde Ambiental
                {" • "}
                Expertise que previne. Processos que controlam. Resultados que protegem.
              </span>

              <span>
                Control Intelligence
              </span>

            </div>

          </div>

        </section>


        {/* ==========================================================
            PAINEL DE LOGIN
            ========================================================== */}

        <section
          className="
            relative
            flex
            h-auto
            min-h-0
            self-start
            flex-col
            items-center
            justify-start
            gap-10
            overflow-visible
            bg-transparent
            pb-16
            lg:gap-12
            px-6
            py-10
            lg:px-10
          "
        >

          {/* Fundo visual vem do canvas fixo global. */}

          {/* ========================================================
              FLUXO DA COLUNA DE ACESSO
              --------------------------------------------------------
              Esta coluna acompanha a rolagem natural do documento.
              Nenhum bloco abaixo do login fica preso à viewport.
              ======================================================== */}

          {/* ========================================================
              CONTEÚDO
              ======================================================== */}

          <div
            className="
              relative
              z-10
              w-full
              max-w-2xl
              my-8
              rounded-[28px]
              border
              border-white/10
              bg-black/[0.18]
              p-8
              shadow-[0_30px_90px_rgba(0,0,0,0.30)]
              backdrop-blur-xl
              transition-all
              duration-500
              hover:border-emerald-300/20
              sm:p-10
              lg:mt-2
              shrink-0
            "
          >

            {/* ======================================================
                MARCA MOBILE
                ====================================================== */}

            <div
              className="
                mb-10
                flex
                items-center
                gap-3
                lg:hidden
              "
            >

              <div
                className="
                  flex
                  shrink-0
                  items-center
                  justify-center
                  overflow-visible
                "
              >
                {brandLogo ? (
                  <img
                    src={brandLogo}
                    alt={brandCompanyName}
                    className="
                      max-h-10
                      max-w-40
                      object-contain
                    "
                    loading="eager"
                    decoding="async"
                    draggable="false"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <ShieldCheck
                    size={23}
                    className="text-primary"
                  />
                )}
              </div>

              <div>

                <div
                  className="
                    text-xl
                    font-black
                    tracking-tight
                  "
                >
                  {brandLoginName}
                </div>

                <div
                  className="
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[0.2em]
                    text-muted-foreground
                  "
                >
                  {brandSystemName}
                </div>

              </div>

            </div>


            {/* ======================================================
                CABEÇALHO
                ====================================================== */}

            <div
              data-scroll-reveal
              data-reveal-delay="1"
              className="
                mb-8
                astral-scroll-reveal
              "
            >

              <div
                className="
                  mb-4
                  flex
                  items-center
                  gap-2
                  text-xs
                  font-medium
                  text-white/55
                "
              >
                <ShieldCheck
                  size={15}
                  className="text-primary"
                />

                Acesso seguro
              </div>

              <h2
                className="
                  text-4xl
                  font-black
                  tracking-tight
                  text-white
                "
              >
                {brandSystemName}
              </h2>

              <p
                className="
                  mt-2
                  text-sm
                  text-white/60
                "
              >
                Entre com suas credenciais corporativas.
              </p>

            </div>


            {/* ======================================================
                FORMULÁRIO DE LOGIN
                ====================================================== */}

            <form
              onSubmit={
                handleLogin
              }
              data-scroll-reveal
              className="
                space-y-5
                astral-scroll-reveal
              "
            >

              {/* ====================================================
                  EMAIL
                  ==================================================== */}

              <div
                data-scroll-reveal
                data-reveal-delay="2"
                className="
                  space-y-2
                  astral-scroll-reveal
                "
              >

                <Label
                  htmlFor="login-email"
                  className="text-white/85"
                >
                  Email
                </Label>

                <Input
                  id="login-email"
                  type="email"
                  autoComplete="off"
                  name="login_email"
                  value={email}
                  onChange={(event) =>
                    setEmail(
                      event.target.value
                    )
                  }
                  placeholder="seu@email.com"
                  disabled={
                    loginLoading
                  }
                  className="
                    h-12
                    border-white/15
                    bg-black/20
                    text-white
                    placeholder:text-white/35
                    caret-emerald-300
                    shadow-inner
                    transition-all
                    duration-300
                    focus-visible:border-emerald-400/60
                    focus-visible:ring-2
                    focus-visible:ring-emerald-400/20
                  "
                />

              </div>


              {/* ====================================================
                  SENHA
                  ==================================================== */}

              <div
                data-scroll-reveal
                data-reveal-delay="2"
                className="
                  space-y-2
                  astral-scroll-reveal
                "
              >

                <div
                  className="
                    flex
                    items-center
                    justify-between
                  "
                >

                  <Label
                    htmlFor="login-password"
                    className="text-white/85"
                  >
                    Senha
                  </Label>

                </div>

                <div
                  className="
                    relative
                  "
                >

                  <Input
                    id="login-password"
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    autoComplete="new-password"
                    name="login_password"
                    value={
                      password
                    }
                    onChange={(event) =>
                      setPassword(
                        event.target.value
                      )
                    }
                    placeholder="••••••••"
                    disabled={
                      loginLoading
                    }
                    className="
                      h-12
                      border-white/15
                      bg-black/20
                      pr-20
                      text-white
                      placeholder:text-white/35
                      caret-emerald-300
                      shadow-inner
                      transition-all
                      duration-300
                      focus-visible:border-emerald-400/60
                      focus-visible:ring-2
                      focus-visible:ring-emerald-400/20
                    "
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        (current) =>
                          !current
                      )
                    }
                    className="
                      absolute
                      right-3
                      top-1/2
                      -translate-y-1/2
                      text-xs
                      font-medium
                      text-white/55
                      hover:text-white
                    "
                  >
                    {showPassword
                      ? "Ocultar"
                      : "Mostrar"}
                  </button>

                </div>

              </div>


              {/* ====================================================
                  ERRO
                  ==================================================== */}

              {error && (
                <div
                  className="
                    flex
                    items-start
                    gap-3
                    rounded-xl
                    border
                    border-destructive/25
                    bg-destructive/10
                    p-3
                    text-sm
                    text-destructive
                  "
                >

                  <AlertCircle
                    size={18}
                    className="
                      mt-0.5
                      shrink-0
                    "
                  />

                  <span>
                    {error}
                  </span>

                </div>
              )}


              {/* ====================================================
                  REMEMBER ME
                  ----------------------------------------------------
                  A opção pertence à sessão, não à autorização.
                  ==================================================== */}

              <div
                data-scroll-reveal
                data-reveal-delay="3"
                className="
                  flex
                  items-center
                  justify-between
                  astral-scroll-reveal
                "
              >
                <label
                  htmlFor="remember-me"
                  className="
                    flex
                    cursor-pointer
                    items-center
                    gap-2.5
                    select-none
                    text-sm
                    text-white/60
                  "
                >
                  <input
                    id="remember-me"
                    name="remember_me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) =>
                      setRememberMe(event.target.checked)
                    }
                    disabled={loginLoading}
                    className="
                      h-4
                      w-4
                      cursor-pointer
                      rounded
                      border-white/20
                      bg-black/20
                      accent-emerald-500
                    "
                    data-testid="remember-me"
                  />

                  <span>Lembrar-me</span>
                </label>
              </div>


              {/* ====================================================
                  BOTÃO ENTRAR
                  ==================================================== */}

              <div
                data-scroll-reveal
                data-reveal-delay="4"
                className="astral-scroll-reveal"
              >
              <Button
                type="submit"
                disabled={
                  loginLoading
                }
                className="
                  h-12
                  w-full
                  border
                  border-emerald-300/25
                  bg-[linear-gradient(135deg,#138a55,#18b978)]
                  text-sm
                  font-bold
                  text-white
                  shadow-[0_12px_30px_rgba(16,185,129,0.18)]
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:brightness-110
                  hover:shadow-[0_16px_36px_rgba(16,185,129,0.25)]
                  active:translate-y-0
                "
              >

                {loginLoading ? (
                  <>
                    <Loader2
                      size={17}
                      className="
                        mr-2
                        animate-spin
                      "
                    />

                    Entrando...
                  </>
                ) : (
                  "Entrar"
                )}

              </Button>
              </div>


              {/* ====================================================
                  ESQUECI SENHA
                  ==================================================== */}

              <div
                data-scroll-reveal
                data-reveal-delay="5"
                className="
                  flex
                  justify-center
                  astral-scroll-reveal
                "
              >

                <button
                  type="button"
                  onClick={() => {
                    setForgotOpen(
                      true
                    );
                    setForgotError(
                      ""
                    );
                    setForgotSuccess(
                      false
                    );
                    setForgotEmail(
                      email
                    );
                  }}
                  className="
                    text-sm
                    font-medium
                    text-emerald-300
                    hover:text-emerald-200
                    hover:underline
                  "
                >
                  Esqueci minha senha
                </button>

              </div>

            </form>


            {/* ======================================================
                PRIMEIRO ACESSO
                ====================================================== */}

            <div
              data-scroll-reveal
              data-reveal-delay="3"
              className="
                my-8
                text-xs
                astral-scroll-reveal
                font-semibold
                uppercase
                tracking-[0.18em]
                text-white/45
              "
            >
              Primeiro acesso
            </div>


            {/* ======================================================
                CARD DE SOLICITAÇÃO
                ====================================================== */}

            <div
              data-scroll-reveal
              data-reveal-delay="6"
              className="
                rounded-2xl
                border
                astral-scroll-reveal
                border-white/10
                bg-white/[0.035]
                p-5
                shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]
                transition-all
                duration-300
                hover:border-emerald-300/20
                hover:bg-white/[0.05]
              "
            >

              <div
                className="
                  flex
                  items-start
                  gap-4
                "
              >

                <div
                  className="
                    flex
                    h-10
                    w-10
                    shrink-0
                    items-center
                    justify-center
                    rounded-xl
                    bg-primary/10
                    text-primary
                  "
                >
                  <UserPlus
                    size={19}
                  />
                </div>

                <div
                  className="
                    min-w-0
                  "
                >

                  <div
                    className="
                      font-bold
                      text-white
                    "
                  >
                    Ainda não possui acesso?
                  </div>

                  <p
                    className="
                      mt-1
                      text-sm
                      leading-relaxed
                      text-white/65
                    "
                  >
                    Solicite seu acesso e aguarde
                    a análise do administrador.
                  </p>

                  <button
                    type="button"
                    onClick={() =>
                      navigate("/login?access=request")
                    }
                    className="
                      mt-3
                      text-sm
                      font-semibold
                      text-emerald-300
                      transition-colors
                      hover:text-emerald-200
                      hover:underline
                    "
                  >
                    Solicitar acesso →
                  </button>
                  <a
                    href="/primeiro-acesso"
                    className="
                      mt-2
                      block
                      text-xs
                      font-medium
                      text-white/55
                      transition-colors
                      hover:text-emerald-300
                    "
                  >
                    Já recebeu o link de aprovação?
                    <span className="ml-1 text-emerald-300">
                      Finalizar cadastro
                    </span>
                  </a>

                </div>

              </div>

            </div>


            {/* ======================================================
                RODAPÉ
                ====================================================== */}

            <div
              data-scroll-reveal
              data-reveal-delay="2"
              className="
                mt-8
                text-center
                astral-scroll-reveal
                text-xs
                text-muted-foreground
              "
            >
              © 2026 Astral Saúde Ambiental
            </div>

          </div>


          {/* ======================================================
              CREDENCIAIS INSTITUCIONAIS
              ------------------------------------------------------
              Esta faixa ocupa o espaço entre o acesso e a biblioteca
              contextual. Os números são parametrizados pela base
              institucional desta página e não são imagens.
              ====================================================== */}

          <section
            data-scroll-reveal
            data-reveal-delay="2"
            aria-label="Informações institucionais"
            className="
              relative
              z-10
              w-full
              max-w-2xl
              astral-scroll-reveal
            "
          >
            <div
              className="
                mb-3
                flex
                items-end
                justify-between
                gap-4
              "
            >
              <div>
                <div
                  className="
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.22em]
                    text-emerald-300/75
                  "
                >
                  A Astral
                </div>

                <h3
                  className="
                    mt-1
                    text-lg
                    font-bold
                    tracking-tight
                    text-white
                  "
                >
                  Experiência que sustenta a operação.
                </h3>
              </div>

              <div
                className="
                  hidden
                  rounded-full
                  border
                  border-emerald-300/10
                  bg-emerald-400/[0.06]
                  px-3
                  py-1.5
                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-[0.16em]
                  text-emerald-200/70
                  sm:block
                "
              >
                Saúde ambiental
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              {INSTITUTIONAL_SLIDES.map((item, index) => {
                const Icon = item.icon;

                return (
                  <article
                    key={`${item.eyebrow}-${item.value}`}
                    data-scroll-reveal
                    data-reveal-delay={String(Math.min(index + 2, 6))}
                    className="
                      group
                      astral-scroll-reveal
                      rounded-2xl
                      border
                      border-white/[0.07]
                      bg-white/[0.025]
                      p-4
                      transition-all
                      duration-500
                      hover:-translate-y-1
                      hover:border-emerald-300/15
                      hover:bg-emerald-300/[0.045]
                    "
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="
                          flex
                          h-9
                          w-9
                          shrink-0
                          items-center
                          justify-center
                          rounded-xl
                          bg-emerald-400/10
                          text-emerald-300
                          transition-transform
                          duration-500
                          group-hover:scale-105
                        "
                      >
                        <Icon size={17} />
                      </div>

                      <div className="min-w-0">
                        <div
                          className="
                            text-[9px]
                            font-bold
                            uppercase
                            tracking-[0.18em]
                            text-emerald-300/65
                          "
                        >
                          {item.eyebrow}
                        </div>

                        <div className="mt-1 flex items-baseline gap-2">
                          <span
                            className="
                              text-xl
                              font-black
                              tracking-tight
                              text-white
                            "
                          >
                            {item.value}
                          </span>

                          <span
                            className="
                              text-[10px]
                              font-semibold
                              uppercase
                              tracking-[0.08em]
                              text-white/55
                            "
                          >
                            {item.title}
                          </span>
                        </div>

                        <p
                          className="
                            mt-1.5
                            text-[11px]
                            leading-5
                            text-white/45
                          "
                        >
                          {item.text}
                        </p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>


          <section
            id="visao-operacional"
            data-scroll-reveal
            data-reveal-delay="3"
            className="
              relative
              z-10
              w-full
              max-w-2xl
              shrink-0
              
              astral-scroll-reveal
            "
          >
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                [ShieldCheck, "Prevenção", "Atuação técnica antes que a ocorrência se torne um problema operacional."],
                [BarChart3, "Indicadores", "Informações organizadas para acompanhar tendência, criticidade e resultado."],
                [MapPinned, "Rastreabilidade", "Histórico das áreas, inspeções, evidências e ações realizadas."],
              ].map(([Icon, title, description]) => (
                <article
                  key={title}
                  className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4 transition-all duration-300 hover:-translate-y-1 hover:border-emerald-300/15 hover:bg-emerald-300/[0.04]"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-300">
                    <Icon size={17} />
                  </div>
                  <h4 className="mt-3 text-sm font-bold text-white">{title}</h4>
                  <p className="mt-1.5 text-[11px] leading-5 text-white/45">{description}</p>
                </article>
              ))}
            </div>
          </section>

          {/* ======================================================
              CONTEÚDO INSTITUCIONAL COMPLETO — ASTRAL
              ------------------------------------------------------
              Este bloco utiliza o conteúdo institucional fornecido
              pelo cliente e ocupa a largura disponível do painel,
              evitando grandes áreas vazias.
              ====================================================== */}

          <section
            id="astral-conteudo-completo"
            data-scroll-reveal
            data-reveal-delay="2"
            className="
              relative
              z-10
              w-full
              max-w-2xl
              shrink-0
              
              astral-scroll-reveal
            "
          >
            <div className="grid gap-6">

              {/* EXPERTISE
                   A apresentação institucional principal já existe
                   na seção "Quem somos". Não repetir este conteúdo. */}


              {/* ESPECIALIZAÇÃO */}
              <article className="rounded-[28px] border border-white/[0.07] bg-black/[0.14] p-6 sm:p-8">
                <div className="grid gap-6 lg:grid-cols-[1fr_.8fr]">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                      Especialização
                    </div>

                    <h2 className="mt-3 text-2xl font-black tracking-tight text-white">
                      Especialização que entende a complexidade da agroindústria.
                    </h2>

                    <p className="mt-4 text-sm leading-7 text-white/60">
                      Operações agroindustriais apresentam condições altamente favoráveis à presença e proliferação
                      de pragas: disponibilidade de alimento, áreas de armazenamento, fluxo intenso de pessoas e
                      materiais, variações de temperatura e umidade, além de estruturas com diferentes níveis de criticidade.
                    </p>

                    <p className="mt-4 text-sm leading-7 text-white/60">
                      Por isso, a ASTRAL trabalha além da resposta imediata à ocorrência. Nossa atuação busca identificar
                      as causas, reduzir fatores de risco e estabelecer barreiras permanentes para o controle das infestações.
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                    {[
                      ["Diagnóstico", "Condições ambientais e estruturais."],
                      ["Risco", "Identificação dos pontos críticos."],
                      ["Prevenção", "Redução dos fatores que favorecem infestações."],
                      ["Auditoria", "Programas alinhados à qualidade e segurança dos alimentos."],
                    ].map(([title, description]) => (
                      <div
                        key={title}
                        className="rounded-2xl border border-white/[0.06] bg-white/[0.025] p-4"
                      >
                        <div className="text-sm font-bold text-white">{title}</div>
                        <p className="mt-1 text-[11px] leading-5 text-white/45">{description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="mt-6 text-sm leading-7 text-white/60">
                  A experiência técnica aplicada à realidade de cada cliente permite construir programas de manejo
                  mais eficientes, compatíveis com os processos produtivos e alinhados às exigências de qualidade,
                  segurança dos alimentos e auditorias.
                </p>
              </article>

              {/* PROCESSO TÉCNICO */}
              <article className="rounded-[28px] border border-emerald-300/10 bg-emerald-400/[0.035] p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-400/10 text-emerald-300">
                    <Activity size={21} />
                  </div>

                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                      Processo técnico
                    </div>
                    <h2 className="mt-2 text-2xl font-black tracking-tight text-white">
                      Processo técnico, controle inteligente.
                    </h2>
                    <p className="mt-3 text-sm leading-7 text-white/60">
                      Nosso diferencial está na combinação entre expertise técnica, metodologia, monitoramento e gestão de informações.
                    </p>
                  </div>
                </div>

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {[
                    "Diagnóstico das condições ambientais e estruturais.",
                    "Identificação e mapeamento dos pontos críticos.",
                    "Análise dos níveis e padrões de infestação.",
                    "Definição de estratégias de controle e prevenção.",
                    "Utilização técnica e responsável de métodos e produtos adequados.",
                    "Monitoramento sistemático das áreas críticas.",
                    "Registro e rastreabilidade das intervenções.",
                    "Análise dos indicadores e evolução dos resultados.",
                    "Ações corretivas e preventivas orientadas por evidências.",
                  ].map((item, index) => (
                    <div
                      key={item}
                      className="flex items-start gap-3 rounded-2xl border border-white/[0.06] bg-black/[0.14] p-4"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-400/10 text-[10px] font-black text-emerald-300">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="text-xs leading-5 text-white/60">{item}</span>
                    </div>
                  ))}
                </div>

                <p className="mt-6 text-sm leading-7 text-white/60">
                  Essa metodologia transforma o controle de pragas em um processo contínuo de gestão,
                  e não em uma simples prestação de serviço.
                </p>
              </article>

              {/* MIP */}
              <article className="rounded-[28px] border border-white/[0.07] bg-white/[0.025] p-6 sm:p-8">
                <div className="grid gap-6 lg:grid-cols-[.85fr_1.15fr]">
                  <div>
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-400/10 text-emerald-300">
                      <ShieldCheck size={21} />
                    </div>

                    <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                      Manejo Integrado de Pragas
                    </div>

                    <h2 className="mt-2 text-2xl font-black tracking-tight text-white">
                      Controle Integrado de Pragas: prevenção antes da infestação.
                    </h2>
                  </div>

                  <div>
                    <p className="text-sm leading-7 text-white/60">
                      A ASTRAL SAÚDE AMBIENTAL atua com os princípios do Manejo Integrado de Pragas (MIP),
                      priorizando a prevenção, o monitoramento e a tomada de decisão baseada em dados.
                    </p>

                    <p className="mt-4 text-sm leading-7 text-white/60">
                      O objetivo é estabelecer um sistema de proteção que dificulte a entrada, o abrigo,
                      a alimentação e a reprodução de pragas, reduzindo a dependência de intervenções corretivas
                      e aumentando a previsibilidade do controle.
                    </p>

                    <p className="mt-4 text-sm leading-7 text-white/60">
                      Em ambientes agroindustriais, essa abordagem contribui para a proteção de diferentes pontos
                      da cadeia operacional, desde áreas externas e recebimento de matérias-primas até produção,
                      armazenagem, expedição e áreas de apoio.
                    </p>
                  </div>
                </div>
              </article>

              {/* RESULTADOS */}
              <article className="rounded-[28px] border border-white/[0.07] bg-black/[0.14] p-6 sm:p-8">
                <div className="grid gap-6 lg:grid-cols-[1fr_.85fr]">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                      Resultados
                    </div>

                    <h2 className="mt-2 text-2xl font-black tracking-tight text-white">
                      Resultados que podem ser medidos.
                    </h2>

                    <p className="mt-4 text-sm leading-7 text-white/60">
                      Para a ASTRAL, eficiência não significa apenas eliminar uma ocorrência.
                      Resultado é reduzir riscos, controlar tendências, prevenir reincidências e demonstrar
                      a evolução do programa.
                    </p>

                    <p className="mt-4 text-sm leading-7 text-white/60">
                      Por meio de monitoramento e indicadores, é possível acompanhar o comportamento das ocorrências,
                      identificar áreas de maior criticidade, avaliar a efetividade das ações implementadas e
                      direcionar recursos para onde eles realmente são necessários.
                    </p>

                    <p className="mt-4 text-sm leading-7 text-white/60">
                      Assim, nossos clientes obtêm maior controle operacional, rastreabilidade, segurança e
                      previsibilidade no gerenciamento de pragas.
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                    {[
                      ["Reduzir riscos", "Atuar sobre causas e fatores críticos."],
                      ["Controlar tendências", "Acompanhar o comportamento das ocorrências."],
                      ["Prevenir reincidências", "Orientar ações por evidências."],
                      ["Demonstrar evolução", "Transformar acompanhamento em indicadores."],
                    ].map(([title, description]) => (
                      <div key={title} className="rounded-2xl bg-white/[0.025] p-4">
                        <div className="text-sm font-bold text-white">{title}</div>
                        <div className="mt-1 text-[11px] leading-5 text-white/45">{description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </article>

              {/* SEGMENTOS */}
              <article
                id="segmentos-astral"
                className="rounded-[28px] border border-white/[0.07] bg-white/[0.025] p-6 sm:p-8"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300/80">
                  Soluções
                </div>

                <h2 className="mt-2 text-2xl font-black tracking-tight text-white">
                  Soluções para diferentes segmentos.
                </h2>

                <p className="mt-3 text-sm leading-7 text-white/55">
                  A experiência da ASTRAL SAÚDE AMBIENTAL permite desenvolver programas de controle
                  para diferentes ambientes e níveis de complexidade.
                </p>

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {[
                    ["Agroindústrias e indústrias de alimentos", "Proteção do processo produtivo e segurança dos alimentos."],
                    ["Armazéns, silos e unidades de armazenamento", "Prevenção de perdas e controle de pontos críticos."],
                    ["Indústrias e centros de distribuição", "Proteção de produtos, estruturas e fluxos logísticos."],
                    ["Empresas, condomínios, estabelecimentos comerciais e instituições", "Programas adequados às características específicas de cada ambiente."],
                  ].map(([title, description]) => (
                    <article
                      key={title}
                      className="rounded-2xl border border-white/[0.06] bg-black/[0.14] p-5"
                    >
                      <div className="flex items-start gap-3">
                        <Building2 className="mt-0.5 shrink-0 text-emerald-300" size={18} />
                        <div>
                          <h3 className="text-sm font-bold leading-5 text-white">{title}</h3>
                          <p className="mt-2 text-xs leading-5 text-white/45">{description}</p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </article>

              {/* FECHAMENTO / CTA
                   O CTA oficial permanece em #fale-conosco. */}


            </div>
          </section>

          {/* ======================================================
              BIBLIOTECA / ESPECIALIDADE SELECIONADA
              ------------------------------------------------------
              Fica deliberadamente mais abaixo, alinhada visualmente
              com a região de Áreas de atuação do painel esquerdo.
              Ao clicar em um tópico, o conteúdo muda aqui sem
              deslocar a página para o topo.
              ====================================================== */}

          {(() => {
            const area = AREA_DETAILS[selectedArea] || AREA_DETAILS["Roedores"];
            const AreaIcon = area.icon;

            return (
              <div
                id="especialidade-astral"
                style={{ scrollMarginTop: "18vh" }}
                data-scroll-reveal
                data-reveal-delay="2"
                className="
                  relative
                  z-10
                  w-full
                  max-w-xl
                  shrink-0
                  rounded-[28px]
                  
                  border
                  border-emerald-300/10
                  bg-black/[0.16]
                  p-7
                  shadow-[0_25px_70px_rgba(0,0,0,0.20)]
                  backdrop-blur-xl
                  astral-scroll-reveal
                  transition-all
                  duration-500
                "
              >
                <div className="flex items-start gap-4">
                  <div
                    className="
                      flex
                      h-12
                      w-12
                      shrink-0
                      items-center
                      justify-center
                      rounded-2xl
                      bg-emerald-400/10
                      text-emerald-300
                      shadow-[0_0_30px_rgba(16,185,129,0.08)]
                    "
                  >
                    <AreaIcon size={22} />
                  </div>

                  <div className="min-w-0">
                    <div
                      className="
                        text-[10px]
                        font-bold
                        uppercase
                        tracking-[0.22em]
                        text-emerald-300/80
                      "
                    >
                      {area.eyebrow}
                    </div>

                    <h3
                      key={`${selectedArea}-title`}
                      className="
                        mt-2
                        text-2xl
                        font-black
                        leading-tight
                        tracking-tight
                        text-white
                        astral-context-switch
                      "
                    >
                      {area.title}
                    </h3>

                    <p
                      key={`${selectedArea}-description`}
                      className="
                        mt-3
                        text-sm
                        leading-7
                        text-white/60
                        astral-context-switch
                      "
                    >
                      {area.description}
                    </p>

                    <div className="mt-5 flex flex-wrap gap-2">
                      {area.highlights.map((item, index) => (
                        <span
                          key={item}
                          className="
                            rounded-full
                            border
                            border-white/[0.07]
                            bg-white/[0.035]
                            px-3
                            py-1.5
                            text-[10px]
                            font-medium
                            text-white/60
                            astral-context-switch
                          "
                          style={{
                            animationDelay: `${index * 70}ms`,
                          }}
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

        </section>

      </div>

      <div
        aria-hidden="true"
        className="
          h-12
          w-full
          bg-transparent
        "
      />


      {/* ============================================================
          TRANSIÇÃO DE LOGIN
          ============================================================ */}

      {transitionVisible && (
        <div
          className="
            fixed
            inset-0
            z-[100]
            flex
            items-center
            justify-center
            bg-slate-950
          "
        >

          <div
            className="
              absolute
              inset-0
              bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.15),transparent_42%)]
            "
          />

          <div
            className="
              relative
              w-full
              max-w-md
              px-6
              text-center
            "
          >

            <div
              className="
                mx-auto
                mb-7
                flex
                items-center
                justify-center
                overflow-visible
              "
            >
              {brandLogo ? (
                <img
                  src={brandLogo}
                  alt={brandCompanyName}
                  className="
                    max-h-16
                    max-w-24
                    object-contain
                  "
                  loading="eager"
                  decoding="async"
                  draggable="false"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <ShieldCheck
                  size={38}
                  className="text-primary"
                />
              )}
            </div>

            <h2
              className="
                text-3xl
                font-black
                text-white
              "
            >
              {brandLoginName}
            </h2>

            <p
              className="
                mt-2
                text-xs
                font-semibold
                uppercase
                tracking-[0.24em]
                text-white/45
              "
            >
              {brandSystemName}
            </p>

            <div
              className="
                mt-10
                space-y-4
                text-left
              "
            >

              {LOGIN_TRANSITION_STEPS.map(
                (
                  step,
                  index
                ) => (
                  <div
                    key={
                      step.label
                    }
                    className={`
                      flex
                      items-center
                      gap-4
                      rounded-xl
                      border
                      px-4
                      py-3
                      transition-all
                      ${
                        index <=
                        transitionStep
                          ? "border-primary/30 bg-primary/10"
                          : "border-white/5 bg-white/[0.02]"
                      }
                    `}
                  >

                    <div
                      className={`
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        ${
                          index <
                          transitionStep
                            ? "bg-primary text-primary-foreground"
                            : index ===
                              transitionStep
                            ? "border border-primary/40 bg-primary/10 text-primary"
                            : "bg-white/5 text-white/25"
                        }
                      `}
                    >

                      {index <
                      transitionStep ? (
                        <CheckCircle2
                          size={16}
                        />
                      ) : index ===
                        transitionStep ? (
                        <Loader2
                          size={16}
                          className="
                            animate-spin
                          "
                        />
                      ) : (
                        <span
                          className="
                            text-xs
                          "
                        >
                          {index + 1}
                        </span>
                      )}

                    </div>

                    <div>

                      <div
                        className={`
                          text-sm
                          font-semibold
                          ${
                            index <=
                            transitionStep
                              ? "text-white"
                              : "text-white/35"
                          }
                        `}
                      >
                        {
                          step.label
                        }
                      </div>

                      <div
                        className="
                          mt-0.5
                          text-xs
                          text-white/40
                        "
                      >
                        {
                          step.sublabel
                        }
                      </div>

                    </div>

                  </div>
                )
              )}

            </div>

          </div>

        </div>
      )}


      {/* ============================================================
          DIALOG — RECUPERAÇÃO DE SENHA
          ============================================================ */}

      <Dialog
        open={forgotOpen}
        onOpenChange={
          setForgotOpen
        }
      >

        <DialogContent
          className="
            sm:max-w-md
          "
        >

          <DialogHeader>

            <DialogTitle>
              Recuperar senha
            </DialogTitle>

          </DialogHeader>

          {forgotSuccess ? (
            <div
              className="
                py-6
                text-center
              "
            >

              <div
                className="
                  mx-auto
                  flex
                  h-14
                  w-14
                  items-center
                  justify-center
                  rounded-full
                  bg-emerald-500/10
                  text-emerald-600
                "
              >
                <CheckCircle2
                  size={28}
                />
              </div>

              <h3
                className="
                  mt-4
                  font-bold
                "
              >
                Solicitação enviada
              </h3>

              <p
                className="
                  mt-2
                  text-sm
                  leading-relaxed
                  text-muted-foreground
                "
              >
                Se o e-mail estiver cadastrado,
                você receberá as instruções para
                redefinir sua senha.
              </p>

              <Button
                type="button"
                className="
                  mt-6
                  w-full
                "
                onClick={() =>
                  setForgotOpen(
                    false
                  )
                }
              >
                Fechar
              </Button>

            </div>
          ) : (
            <form
              onSubmit={
                handleForgotPassword
              }
              className="
                space-y-5
              "
            >

              <p
                className="
                  text-sm
                  leading-relaxed
                  text-muted-foreground
                "
              >
                Informe o e-mail utilizado
                no seu cadastro para receber
                as instruções de recuperação.
              </p>

              <div
                className="
                  space-y-2
                "
              >

                <Label
                  htmlFor="forgot-email"
                >
                  E-mail
                </Label>

                <Input
                  id="forgot-email"
                  type="email"
                  value={
                    forgotEmail
                  }
                  onChange={(event) =>
                    setForgotEmail(
                      event.target.value
                    )
                  }
                  placeholder="seu@email.com"
                  disabled={
                    forgotLoading
                  }
                />

              </div>

              {forgotError && (
                <div
                  className="
                    flex
                    items-start
                    gap-3
                    rounded-xl
                    border
                    border-destructive/25
                    bg-destructive/10
                    p-3
                    text-sm
                    text-destructive
                  "
                >

                  <AlertCircle
                    size={17}
                    className="
                      mt-0.5
                      shrink-0
                    "
                  />

                  <span>
                    {forgotError}
                  </span>

                </div>
              )}

              <div
                className="
                  flex
                  gap-3
                "
              >

                <Button
                  type="button"
                  variant="outline"
                  className="
                    flex-1
                  "
                  disabled={
                    forgotLoading
                  }
                  onClick={() =>
                    setForgotOpen(
                      false
                    )
                  }
                >
                  Cancelar
                </Button>

                <Button
                  type="submit"
                  className="
                    flex-1
                  "
                  disabled={
                    forgotLoading
                  }
                >

                  {forgotLoading ? (
                    <>
                      <Loader2
                        size={16}
                        className="
                          mr-2
                          animate-spin
                        "
                      />

                      Enviando...
                    </>
                  ) : (
                    "Enviar"
                  )}

                </Button>

              </div>

            </form>
          )}

        </DialogContent>

      </Dialog>




      </div>
    </div>
  );
}

/*
 * ================================================================
 * LOGIN ROUTE / PRIMEIRO ACESSO
 * ================================================================
 *
 * Hooks nunca são chamados condicionalmente.
 *
 * /login                    -> LoginPage
 * /login?token=...          -> FirstAccess
 * /primeiro-acesso?token=.. -> FirstAccess
 *
 * O FirstAccess.jsx existente continua sendo a segunda tela oficial.
 */
export default function Login() {
  const location = useLocation();

  const searchParams = new URLSearchParams(
    location.search
  );

  const token =
    searchParams.get("token");

  const isAccessRequest =
    searchParams.get("access") === "request";

  if (isAccessRequest) {
    return <AccessRequest />;
  }

  const isFirstAccessPath =
    location.pathname ===
      "/primeiro-acesso" ||
    location.pathname ===
      "/primeiro_acesso";

  if (
    token &&
    (
      isFirstAccessPath ||
      location.pathname === "/login"
    )
  ) {
    return <FirstAccess />;
  }

  return <LoginPage />;
}